import React from "react";
import Chart from "react-apexcharts";
import Card from "components/card";
import { dteDocumentsApi } from "api/endpoints";
import { useBodyDarkClass } from "hooks/useBodyDarkClass";
import { useShallow } from "zustand/react/shallow";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";

const DAY_LABELS = [
  { key: 1, label: "Lunes" },
  { key: 2, label: "Martes" },
  { key: 3, label: "Miércoles" },
  { key: 4, label: "Jueves" },
  { key: 5, label: "Viernes" },
  { key: 6, label: "Sábado" },
];
const HOUR_LABELS = Array.from(
  { length: 11 },
  (_, index) => `${String(9 + index).padStart(2, "0")}:00`,
);

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatInt(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("es-CL").format(n);
}

function buildHeatmapSeries(items = [], summary = {}) {
  const bucketMap = new Map();
  const dayTotals = new Map();

  for (const item of items) {
    const dayKey = Number(item?.day_of_week);
    const hour = Number(item?.hour_of_day);
    const amount = Number(item?.net_amount || 0);
    if (!Number.isInteger(dayKey) || !Number.isInteger(hour)) continue;
    if (hour < 9 || hour > 19) continue;
    const mapKey = `${dayKey}-${hour}`;
    bucketMap.set(mapKey, amount);
    dayTotals.set(dayKey, (dayTotals.get(dayKey) || 0) + amount);
  }

  const series = DAY_LABELS.map((day) => ({
    name: day.label,
    data: HOUR_LABELS.map((hourLabel, hour) => ({
      x: hourLabel,
      y: Number(bucketMap.get(`${day.key}-${9 + hour}`) || 0),
    })),
  }));

  let peakBucket = null;
  for (const row of series) {
    for (const point of row.data) {
      if (!peakBucket || point.y > peakBucket.amount) {
        peakBucket = {
          day: row.name,
          hour: point.x,
          amount: point.y,
        };
      }
    }
  }

  return {
    series,
    dayTotals: DAY_LABELS.map((day) => ({
      label: day.label,
      amount: Number(dayTotals.get(day.key) || 0),
    })),
    totalSales: Number(summary?.total_sales || 0),
    totalOrders: Number(summary?.total_orders || 0),
    peakBucket,
    maxCellAmount: Math.max(
      0,
      ...series.flatMap((row) => row.data.map((point) => Number(point.y || 0))),
    ),
    minCellAmount: Math.min(
      0,
      ...series.flatMap((row) => row.data.map((point) => Number(point.y || 0))),
    ),
  };
}

function buildChartOptions(isDark, maxCellAmount = 0, minCellAmount = 0) {
  const fg = isDark ? "#e2e8f0" : "#334155";
  const muted = isDark ? "#94a3b8" : "#64748b";
  const border = isDark ? "rgba(148,163,184,0.16)" : "rgba(148,163,184,0.18)";
  const safeMax = Math.max(0, Number(maxCellAmount || 0));
  const safeMin = Math.min(0, Number(minCellAmount || 0));
  const p1 = safeMax > 0 ? Math.max(1, Math.round(safeMax * 0.15)) : 1;
  const p2 = safeMax > 0 ? Math.max(p1 + 1, Math.round(safeMax * 0.3)) : 2;
  const p3 = safeMax > 0 ? Math.max(p2 + 1, Math.round(safeMax * 0.5)) : 3;
  const p4 = safeMax > 0 ? Math.max(p3 + 1, Math.round(safeMax * 0.7)) : 4;
  const p5 = safeMax > 0 ? Math.max(p4 + 1, Math.round(safeMax * 0.85)) : 5;
  const n1 = safeMin < 0 ? Math.min(-1, Math.round(safeMin * 0.15)) : -1;
  const n2 = safeMin < 0 ? Math.min(n1 - 1, Math.round(safeMin * 0.3)) : -2;
  const n3 = safeMin < 0 ? Math.min(n2 - 1, Math.round(safeMin * 0.5)) : -3;
  const n4 = safeMin < 0 ? Math.min(n3 - 1, Math.round(safeMin * 0.7)) : -4;
  const n5 = safeMin < 0 ? Math.min(n4 - 1, Math.round(safeMin * 0.85)) : -5;
  return {
    chart: {
      type: "heatmap",
      toolbar: { show: false },
      background: "transparent",
      foreColor: fg,
      fontFamily: "inherit",
    },
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: {
      width: 1,
      colors: [isDark ? "rgba(15,23,42,0.55)" : "rgba(255,255,255,0.8)"],
    },
    plotOptions: {
      heatmap: {
        radius: 6,
        shadeIntensity: 0.35,
        colorScale: {
          ranges: [
            {
              from: safeMin,
              to: n5,
              color: "#7f1d1d",
              name: "NC extrema",
            },
            {
              from: n5 + 1,
              to: n4,
              color: "#b91c1c",
              name: "NC muy alta",
            },
            {
              from: n4 + 1,
              to: n3,
              color: "#dc2626",
              name: "NC alta",
            },
            {
              from: n3 + 1,
              to: n2,
              color: "#f87171",
              name: "NC media",
            },
            {
              from: n2 + 1,
              to: n1,
              color: "#fecaca",
              name: "NC leve",
            },
            {
              from: Math.min(0, n1 + 1),
              to: 0,
              color: isDark ? "#475569" : "#e2e8f0",
              name: "Sin ventas",
            },
            { from: 1, to: p1, color: "#ecfeff", name: "Venta muy baja" },
            { from: p1 + 1, to: p2, color: "#a7f3d0", name: "Venta baja" },
            { from: p2 + 1, to: p3, color: "#34d399", name: "Venta media" },
            { from: p3 + 1, to: p4, color: "#10b981", name: "Venta alta" },
            {
              from: p4 + 1,
              to: p5,
              color: "#059669",
              name: "Venta muy alta",
            },
            {
              from: p5 + 1,
              to: Math.max(p5 + 1, safeMax),
              color: "#065f46",
              name: "Hora punta",
            },
          ],
        },
      },
    },
    xaxis: {
      labels: {
        rotate: 0,
        style: { colors: muted, fontSize: "10px" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: muted, fontSize: "11px" },
      },
    },
    grid: {
      borderColor: border,
      padding: { left: 8, right: 8 },
    },
    tooltip: {
      theme: isDark ? "dark" : "light",
      y: {
        formatter: (val) => formatMoney(val),
      },
    },
  };
}

export default function HourlySalesHeatmap({ desde, hasta }) {
  const isDark = useBodyDarkClass();
  const emission = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      ambiente: s.ambiente,
      profileUuid: s.profileUuid,
      rutEmisor: s.rutEmisor,
    })),
  );
  const selectedAmbiente =
    emission.kind === "ready" && emission.ambiente ? emission.ambiente : "";
  const selectedProfileUuid =
    emission.kind === "ready" && emission.profileUuid ? String(emission.profileUuid).trim() : "";
  const selectedRutEmisor =
    emission.kind === "ready" && emission.rutEmisor ? String(emission.rutEmisor).trim() : "";

  const [state, setState] = React.useState({
    loading: true,
    error: null,
    items: [],
    summary: {
      total_sales: 0,
      total_orders: 0,
    },
  });

  React.useEffect(() => {
    if (!selectedAmbiente) {
      setState({
        loading: false,
        error: null,
        items: [],
        summary: {
          total_sales: 0,
          total_orders: 0,
        },
      });
      return;
    }
    let cancelled = false;
    (async () => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const qs = new URLSearchParams({
          desde,
          hasta,
          modo: selectedAmbiente,
          ...(selectedProfileUuid ? { profileUuid: selectedProfileUuid } : {}),
          ...(selectedRutEmisor ? { rut_emisor: selectedRutEmisor } : {}),
        });
        const { res, data } = await dteDocumentsApi.salesHeatmap(`?${qs.toString()}`);
        if (cancelled) return;
        if (!res?.ok || data?.ok === false) {
          setState({
            loading: false,
            error: data?.error || data?.message || `Error ${res?.status || "desconocido"}`,
            items: [],
            summary: {
              total_sales: 0,
              total_orders: 0,
            },
          });
          return;
        }
        setState({
          loading: false,
          error: null,
          items: Array.isArray(data?.items) ? data.items : [],
          summary: data?.summary || {
            total_sales: 0,
            total_orders: 0,
          },
        });
      } catch (error) {
        if (cancelled) return;
        setState({
          loading: false,
          error: error instanceof Error ? error.message : "Error de red.",
          items: [],
          summary: {
            total_sales: 0,
            total_orders: 0,
          },
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [desde, hasta, selectedAmbiente, selectedProfileUuid, selectedRutEmisor]);

  const computed = React.useMemo(
    () => buildHeatmapSeries(state.items, state.summary),
    [state.items, state.summary],
  );
  const options = React.useMemo(
    () => buildChartOptions(isDark, computed.maxCellAmount, computed.minCellAmount),
    [isDark, computed.maxCellAmount, computed.minCellAmount],
  );

  return (
    <Card extra="rounded-[20px] border border-gray-200/70 p-6 dark:border-white/10 xl:col-span-3">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            Mapa de valor por hora
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Ventas aceptadas por día de la semana y hora, usando el mismo rango global del dashboard.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 font-semibold text-gray-700 dark:border-white/10 dark:bg-white/5 dark:text-gray-200">
            Total ventas: {formatMoney(computed.totalSales)}
          </span>
          <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 font-semibold text-gray-700 dark:border-white/10 dark:bg-white/5 dark:text-gray-200">
            Órdenes: {formatInt(computed.totalOrders)}
          </span>
          {computed.peakBucket?.amount > 0 ? (
            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 font-semibold text-blue-800 dark:border-blue-400/20 dark:bg-blue-500/10 dark:text-blue-200">
              Punta: {computed.peakBucket.day} · {computed.peakBucket.hour}
            </span>
          ) : null}
        </div>
      </div>

      {state.loading ? (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          Cargando mapa horario…
        </p>
      ) : state.error ? (
        <p className="py-6 text-center text-sm text-red-600 dark:text-red-400">{state.error}</p>
      ) : computed.totalOrders === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
          No hay ventas aceptadas suficientes en el rango para construir el mapa horario.
        </p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-2 text-[11px] text-gray-500 dark:text-gray-400">
            <span>
              Cada celda representa el <span className="font-semibold">monto total vendido</span> en esa hora.
            </span>
            <span>
              Horario analizado: <span className="font-semibold">09:00 a 19:00</span>.
            </span>
          </div>
          <div className="mb-4 flex flex-wrap gap-2 text-[11px]">
            {computed.dayTotals.map((day) => (
              <span
                key={day.label}
                className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 font-medium text-gray-600 dark:border-white/10 dark:bg-white/5 dark:text-gray-300"
              >
                {day.label}: {formatMoney(day.amount)}
              </span>
            ))}
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[860px]">
              <Chart
                type="heatmap"
                options={options}
                series={computed.series}
                height={360}
              />
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
